import {
  all,
  put,
  takeLatest,
  takeEvery,
  take,
  race,
  call,
  delay,
  select
} from 'redux-saga/effects';
import { MemberLookupActionTypes, MemberLookupState } from './types';
import {
  registerMemberLookupSuccess,
  registerMemberLookupFailure,
  uploadFileSuccess,
  uploadFileFailure,
  pollJobStart,
  pollJobUpdate,
  pollJobComplete,
  pollJobFailure,
  fetchLivePriceSuccess,
  fetchLivePriceFailure,
  createCheckoutSuccess,
  createCheckoutFailure,
  pollCheckoutStatusComplete,
  pollCheckoutStatusFailure,
  pollMemberLookupStatusStart,
  pollMemberLookupStatusUpdate,
  pollMemberLookupStatusComplete,
  pollMemberLookupStatusFailure,
  fetchDownloadUrlSuccess,
  fetchDownloadUrlFailure,
  restoreLookupId
} from './actions';
import {
  getUploadUrl,
  uploadFileToS3,
  registerMemberLookup,
  getLivePrice,
  createCheckoutSession,
  getCheckoutStatus,
  getMemberLookupStatus,
  getJobStatus,
  getDownloadUrl,
  RegisterMemberLookupResponse,
  LivePriceResponse,
  CreateCheckoutResponse,
  CheckoutStatusResponse,
  MemberLookupStatusResponse,
  JobStatusResponse,
  DownloadResponse
} from 'services/bydaMemberLookup.service';
import { APP_URL } from 'types/app.type';
import {
  BYDA_MEMBER_LOOKUP_SUPPORT_EMAIL,
  LOOKUP_TIMEOUT_ERROR,
  formatFileValidationFailedMessage,
  formatFileValidationTimeoutMessage,
  formatNoValidShapesMessage,
  formatUserFacingErrorCode
} from 'modules/bydaMemberLookup/constants/errorMessages';
const getMemberLookupState = (state: any) => state.memberLookupCache;
const getSession = (state: any) => state.session;

const CHECKOUT_LOOKUP_ID_STORAGE_KEY = 'byda_member_lookup_checkout_id';

function readStoredLookupId(): string | null {
  return sessionStorage.getItem(CHECKOUT_LOOKUP_ID_STORAGE_KEY);
}

function clearStoredLookupId(): void {
  sessionStorage.removeItem(CHECKOUT_LOOKUP_ID_STORAGE_KEY);
}

function* doUploadFile(action: { type: string; payload: { file: File } }) {
  const { file } = action.payload;
  try {
    const { uploadUrl, fileId } = yield call(getUploadUrl, file.name);

    const s3Response: Response = yield call(uploadFileToS3, uploadUrl, file);

    if (!s3Response.ok) {
      yield put(
        uploadFileFailure(
          'File upload failed. Please check your connection and try again.'
        )
      );
      return;
    }

    yield put(uploadFileSuccess(fileId));
    yield put({
      type: MemberLookupActionTypes.REGISTER_START,
      payload: { inputSource: 'FILE_UPLOAD', fileId }
    });
  } catch {
    yield put(
      uploadFileFailure(
        'File upload failed. Please check your connection and try again.'
      )
    );
  }
}

function* watchUploadFile() {
  yield takeLatest(MemberLookupActionTypes.UPLOAD_FILE_START, doUploadFile);
}

function* doRegisterMemberLookup(action: { type: string; payload: any }) {
  try {
    const response: RegisterMemberLookupResponse = yield call(
      registerMemberLookup,
      action.payload
    );
    yield put(
      registerMemberLookupSuccess({
        lookupId: response.lookupId,
        registrationJobId: response.jobId
      })
    );
    if (response.jobId) {
      yield put(pollJobStart(response.jobId));
    }
  } catch (err) {
    const errAny = err || {};
    const status = errAny.status ?? errAny.response?.status;
    let message = 'Something went wrong. Please try again.';
    if (status === 0) {
      message =
        'Unable to reach the server. Please check your connection and try again.';
    } else if (status === 400) {
      if (action.payload.inputSource === 'MAP_SKETCH') {
        message =
          'One or more shapes are invalid. Please redraw and try again.';
      } else {
        message =
          'The file could not be processed. Please check your file and try again.';
      }
    } else if (status === 401) {
      message = 'Your session has expired. Please log in again.';
    } else if (status === 403) {
      message = 'You do not have permission to perform this action.';
    }
    yield put(registerMemberLookupFailure(message));
  }
}

function* watchRegisterMemberLookup() {
  yield takeLatest(
    MemberLookupActionTypes.REGISTER_START,
    doRegisterMemberLookup
  );
}

const POLL_INTERVAL_FAST_MS = 1000;
const POLL_INTERVAL_SLOW_MS = 3000;
const FAST_POLL_DURATION_MS = 10_000;
const MAX_BACKOFF_MS = 30_000;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000;

function* doPollJob(action: { type: string; payload: number }) {
  const jobId: number = action.payload;

  if (!jobId) {
    yield put(
      pollJobFailure('Processing could not be started. Please try again.')
    );
    return;
  }

  let consecutiveFailures = 0;
  let elapsedMs = 0;
  let intervalMs = POLL_INTERVAL_FAST_MS;

  while (true) {
    const { reset } = yield race({
      tick: delay(intervalMs),
      reset: take(MemberLookupActionTypes.RESET)
    });
    if (reset) return;
    elapsedMs += intervalMs;

    try {
      const job: JobStatusResponse = yield call(getJobStatus, jobId);
      consecutiveFailures = 0;
      intervalMs =
        elapsedMs < FAST_POLL_DURATION_MS
          ? POLL_INTERVAL_FAST_MS
          : POLL_INTERVAL_SLOW_MS;

      yield put(pollJobUpdate(job.status));

      if (job.status === 'FAILED') {
        const jobResponse: any = job.response;
        const jobResponseError = jobResponse?.error;
        const serverMessage = jobResponseError ? ` ${jobResponseError}.` : '';
        const memberLookupState: MemberLookupState = yield select(
          getMemberLookupState
        );
        const idSuffix = formatUserFacingErrorCode(jobId);
        const isFileValidation =
          memberLookupState.processingPhase === 'VALIDATING_FILE' ||
          memberLookupState.processingPhase === 'FILE_VALIDATED';

        yield put(
          pollJobFailure(
            isFileValidation
              ? formatFileValidationFailedMessage(jobResponseError, idSuffix)
              : `Processing failed.${serverMessage} Please try again or contact ${BYDA_MEMBER_LOOKUP_SUPPORT_EMAIL}.${idSuffix}`
          )
        );
        return;
      }

      if (job.status === 'COMPLETED') {
        const memberLookupState: MemberLookupState = yield select(
          getMemberLookupState
        );
        let fileSummary: { shapeCount?: number; areaKm2?: number } = {};

        if (memberLookupState.lookupId) {
          try {
            const status: MemberLookupStatusResponse = yield call(
              getMemberLookupStatus,
              memberLookupState.lookupId
            );
            if (
              typeof status.shapeCount === 'number' &&
              typeof status.areaKm2 === 'number'
            ) {
              fileSummary = {
                shapeCount: status.shapeCount,
                areaKm2: status.areaKm2
              };
            }
          } catch {
            // Summary stats are optional.
          }
        }

        if (
          memberLookupState.processingPhase === 'VALIDATING_FILE' &&
          fileSummary.shapeCount === 0
        ) {
          yield put(pollJobFailure(formatNoValidShapesMessage()));
          return;
        }

        yield put(pollJobComplete(fileSummary));
        return;
      }
    } catch {
      consecutiveFailures++;
      intervalMs = Math.min(intervalMs * 2, MAX_BACKOFF_MS);

      if (consecutiveFailures >= 3) {
        yield put(pollJobUpdate('PROCESSING'));
      }
    }

    if (elapsedMs >= MAX_POLL_DURATION_MS) {
      yield put(
        pollJobFailure(
          formatFileValidationTimeoutMessage(formatUserFacingErrorCode(jobId))
        )
      );
      return;
    }
  }
}

function* watchPollJob() {
  yield takeLatest(MemberLookupActionTypes.POLL_JOB_START, doPollJob);
}

function* doFetchLivePrice() {
  try {
    const response: LivePriceResponse = yield call(getLivePrice);
    yield put(
      fetchLivePriceSuccess({
        ...response,
        priceIncludeTax: response.taxBehavior === 'inclusive'
      })
    );
  } catch {
    yield put(fetchLivePriceFailure('Unable to fetch price.'));
  }
}

function* watchFetchLivePrice() {
  yield takeLatest(
    MemberLookupActionTypes.FETCH_LIVE_PRICE_START,
    doFetchLivePrice
  );
}

function* doCreateCheckout(action: { type: string; payload: number }) {
  const lookupId: number = action.payload;
  try {
    sessionStorage.setItem(CHECKOUT_LOOKUP_ID_STORAGE_KEY, String(lookupId));

    const session = yield select(getSession);
    const userId = session?.currentUser?.id;
    if (!userId) {
      yield put(
        createCheckoutFailure('Your session has expired. Please log in again.')
      );
      return;
    }

    const returnUrl =
      window.location.origin +
      APP_URL.BYDA_MEMBER_LOOKUP +
      '?session_id={CHECKOUT_SESSION_ID}';
    const response: CreateCheckoutResponse = yield call(
      createCheckoutSession,
      lookupId,
      userId,
      returnUrl
    );

    if (response.clientSecret) {
      yield put(
        createCheckoutSuccess({
          clientSecret: response.clientSecret,
          transactionReference: response.transactionReference
        })
      );
      return;
    }

    if (response.completed) {
      yield put(
        createCheckoutSuccess({
          completed: true,
          postPaymentId: response.postPaymentId
            ? Number(response.postPaymentId)
            : undefined,
          transactionReference: response.transactionReference
        })
      );
      return;
    }

    yield put(createCheckoutFailure('Unexpected payment response.'));
  } catch (err) {
    const errAny = err || {};
    const status = errAny.status ?? errAny.response?.status;
    let message = 'Something went wrong. Please try again.';
    if (status === 0) {
      message =
        'Unable to reach the server. Please check your connection and try again.';
    } else if (status === 400) {
      message = 'Unable to create payment session. Please try again.';
    }
    yield put(createCheckoutFailure(message));
  }
}

function* watchCreateCheckout() {
  yield takeLatest(
    MemberLookupActionTypes.CREATE_CHECKOUT_START,
    doCreateCheckout
  );
}

function* doConfirmPostpay() {
  const memberLookupState: MemberLookupState = yield select(
    getMemberLookupState
  );
  const lookupId = memberLookupState.lookupId;
  if (!lookupId) {
    yield put(
      pollMemberLookupStatusFailure(
        'Lookup session is missing. Please try again.'
      )
    );
    return;
  }
  yield put(pollMemberLookupStatusStart(lookupId));
}

function* watchConfirmPostpay() {
  yield takeLatest(MemberLookupActionTypes.CONFIRM_POSTPAY, doConfirmPostpay);
}

const CHECKOUT_POLL_INTERVAL_MS = 2000;
const MAX_CHECKOUT_POLL_DURATION_MS = 2 * 60 * 1000;

function* doPollCheckoutStatus(action: { type: string; payload: string }) {
  const sessionId: string = action.payload;
  let elapsedMs = 0;

  while (true) {
    const { reset } = yield race({
      tick: delay(CHECKOUT_POLL_INTERVAL_MS),
      reset: take(MemberLookupActionTypes.RESET)
    });
    if (reset) return;
    elapsedMs += CHECKOUT_POLL_INTERVAL_MS;

    try {
      const result: CheckoutStatusResponse = yield call(
        getCheckoutStatus,
        sessionId
      );

      if (result.status === 'complete') {
        yield put(
          pollCheckoutStatusComplete({
            paymentIntentId: result.paymentIntentId
          })
        );

        const memberLookupState: MemberLookupState = yield select(
          getMemberLookupState
        );
        const storedLookupId = readStoredLookupId();
        const lookupId: number | null =
          memberLookupState.lookupId ??
          (storedLookupId ? Number(storedLookupId) : null);

        clearStoredLookupId();

        if (!lookupId) {
          yield put(
            pollMemberLookupStatusFailure(
              'Lookup session is missing. Please try again.'
            )
          );
          return;
        }

        if (!memberLookupState.lookupId) {
          yield put(restoreLookupId(lookupId));
        }

        yield put(pollMemberLookupStatusStart(lookupId));
        return;
      }

      if (result.status === 'expired') {
        const memberLookupState: MemberLookupState = yield select(
          getMemberLookupState
        );
        const storedLookupId = readStoredLookupId();
        const lookupIdFromStorage = storedLookupId
          ? Number(storedLookupId)
          : null;

        if (!memberLookupState.lookupId && lookupIdFromStorage) {
          yield put(restoreLookupId(lookupIdFromStorage));
        }

        yield put(
          pollCheckoutStatusFailure(
            'EXPIRED: Your payment session has expired. Please try again.'
          )
        );
        return;
      }
    } catch {
      // Silent retry on network errors
    }

    if (elapsedMs >= MAX_CHECKOUT_POLL_DURATION_MS) {
      const memberLookupState: MemberLookupState = yield select(
        getMemberLookupState
      );
      const storedLookupId = readStoredLookupId();
      const lookupIdFromStorage = storedLookupId
        ? Number(storedLookupId)
        : null;

      if (!memberLookupState.lookupId && lookupIdFromStorage) {
        yield put(restoreLookupId(lookupIdFromStorage));
      }

      yield put(
        pollCheckoutStatusFailure(
          'Payment confirmation is taking longer than expected. Please wait or check back later.'
        )
      );
      return;
    }
  }
}

function* watchPollCheckoutStatus() {
  yield takeLatest(
    MemberLookupActionTypes.POLL_CHECKOUT_STATUS_START,
    doPollCheckoutStatus
  );
}

function* doPollMemberLookupStatus(action: { type: string; payload: number }) {
  const lookupId: number = action.payload;

  if (!lookupId) {
    yield put(
      pollMemberLookupStatusFailure(
        'Lookup session is missing. Please try again.'
      )
    );
    return;
  }

  let consecutiveFailures = 0;
  let elapsedMs = 0;
  let intervalMs = POLL_INTERVAL_FAST_MS;

  while (true) {
    const { reset } = yield race({
      tick: delay(intervalMs),
      reset: take(MemberLookupActionTypes.RESET)
    });
    if (reset) return;
    elapsedMs += intervalMs;

    try {
      const result: MemberLookupStatusResponse = yield call(
        getMemberLookupStatus,
        lookupId
      );
      consecutiveFailures = 0;
      intervalMs =
        elapsedMs < FAST_POLL_DURATION_MS
          ? POLL_INTERVAL_FAST_MS
          : POLL_INTERVAL_SLOW_MS;

      yield put(pollMemberLookupStatusUpdate(result.status));

      if (result.status === 'FAILED') {
        yield put(
          pollMemberLookupStatusFailure(
            `Processing failed. Please try again or contact ${BYDA_MEMBER_LOOKUP_SUPPORT_EMAIL}.`
          )
        );
        return;
      }

      if (result.status === 'COMPLETED') {
        yield put(pollMemberLookupStatusComplete());
        yield put({
          type: MemberLookupActionTypes.FETCH_DOWNLOAD_URL_START,
          payload: lookupId
        });
        return;
      }
    } catch {
      consecutiveFailures++;
      intervalMs = Math.min(intervalMs * 2, MAX_BACKOFF_MS);

      if (consecutiveFailures >= 3) {
        yield put(pollMemberLookupStatusUpdate('PROCESSING'));
      }
    }

    if (elapsedMs >= MAX_POLL_DURATION_MS) {
      yield put(pollMemberLookupStatusFailure(LOOKUP_TIMEOUT_ERROR));
      return;
    }
  }
}

function* watchPollMemberLookupStatus() {
  yield takeLatest(
    MemberLookupActionTypes.POLL_STATUS_START,
    doPollMemberLookupStatus
  );
}

function* doFetchDownloadUrl(action: { type: string; payload: number }) {
  const lookupId: number = action.payload;
  try {
    const response: DownloadResponse = yield call(getDownloadUrl, lookupId);
    yield put(
      fetchDownloadUrlSuccess({
        url: response.url,
        memberCount: response.memberCount,
        fileName: response.fileName
      })
    );
  } catch (err) {
    const errAny = err || {};
    const status = errAny.status ?? errAny.response?.status;
    let message = 'Download failed. Please try again.';
    if (status === 403) {
      message = 'You do not have permission to download this result.';
    } else if (status === 404) {
      message = 'The result is not yet available. Please wait and try again.';
    }
    yield put(fetchDownloadUrlFailure(message));
  }
}

function* watchFetchDownloadUrl() {
  yield takeLatest(
    MemberLookupActionTypes.FETCH_DOWNLOAD_URL_START,
    doFetchDownloadUrl
  );
}

function* clearStoredLookupIdSaga() {
  yield call(clearStoredLookupId);
}

function* watchReset() {
  yield takeEvery(MemberLookupActionTypes.RESET, clearStoredLookupIdSaga);
}

export default function* memberLookupSagas() {
  yield all([
    call(watchUploadFile),
    call(watchRegisterMemberLookup),
    call(watchPollJob),
    call(watchFetchLivePrice),
    call(watchCreateCheckout),
    call(watchConfirmPostpay),
    call(watchPollCheckoutStatus),
    call(watchPollMemberLookupStatus),
    call(watchFetchDownloadUrl),
    call(watchReset)
  ]);
}
